import { atom } from 'jotai';
import { createResourceAtom } from 'lib/atoms';
import request from 'lib/request';
import type {
  MatchReviewResponse,
  WellLifecycleResponse,
  WellOverviewResponse,
} from '../types-well';
import { buildSectionMutationAtom } from 'lib/atoms/section-mutation';

// ---------------------------------------------------------------------------
// DO Well Review data layer. Mirrors `afe-catalog/structuring/atoms/wells.ts`
// and the sibling `./interests.ts`:
//   • the Overview list lives in a *module-level* atom so the shell nav, the
//     overview table, and Match Review all share one piece of state and any
//     mutation can trigger a refetch that updates them;
//   • the Match Review detail uses the codebase `createResourceAtom` factory
//     (auto-fetches on mount, setter refetches);
//   • confirm / revert / change-match / rerun / add / edit / remove / restore
//     are uniform { url, method, data } mutation atoms built from the SHARED
//     `buildSectionMutationAtom` (from `lib/atoms` — never recreated).
//
// The DO drill-in SELECTION state is DOCUMENT-SCOPED and lives in `./layout`
// (selectedWellIndexAtom) — it is intentionally NOT declared here so DO does not
// reuse AFE's module-scoped drill-in singletons.
// ---------------------------------------------------------------------------

// ── Overview (GET /documents/:documentId/division_order_structuring/wells) ────

export const wellOverviewDataAtom = atom<WellOverviewResponse | null>(null);
export const wellOverviewLoadingAtom = atom(false);
// Load-failure message. Non-null means the last fetch failed — the section shows
// an error banner instead of an empty table, so a 5xx/network failure is never
// silently indistinguishable from a DO that genuinely has no staged wells.
export const wellOverviewErrorAtom = atom<string | null>(null);

// The most-recent fetch target. The data atom is module-level (shared across DO
// documents), so a stale in-flight response — the previous document's fetch
// resolving after a document switch, or an older reload superseded by a newer
// one — must not commit over the current well list. Dropped unless it is still
// the latest requested href.
const wellOverviewRequestedHrefAtom = atom<string | undefined>(undefined);

export const fetchWellOverviewAtom = atom(
  null,
  async (get, set, href: string | undefined) => {
    if (!href) return;
    set(wellOverviewRequestedHrefAtom, href);
    set(wellOverviewLoadingAtom, true);
    set(wellOverviewErrorAtom, null);
    try {
      const response = await request.get<WellOverviewResponse>(href);
      // Drop a response that is no longer the latest requested target.
      if (get(wellOverviewRequestedHrefAtom) !== href) return;
      if ('error' in response) {
        console.error('Failed to fetch DO well overview:', response.error);
        set(wellOverviewErrorAtom, 'Unable to load wells.');
      } else {
        set(wellOverviewDataAtom, response);
      }
    } catch (error) {
      if (get(wellOverviewRequestedHrefAtom) !== href) return;
      console.error('Failed to fetch DO well overview:', error);
      set(wellOverviewErrorAtom, 'Unable to load wells.');
    } finally {
      // Only the latest fetch clears the loading flag.
      if (get(wellOverviewRequestedHrefAtom) === href) {
        set(wellOverviewLoadingAtom, false);
      }
    }
  }
);

export const wellOverviewUrl = (documentId: number | string) =>
  `/documents/${documentId}/division_order_structuring/wells`;

// ── Match Review detail (GET .../division_order_structuring/wells/:id) ────────
//
// :id is a NUMERIC DivisionOrderTarget PK (the controller does
// `@document.division_order_targets.find(params[:id])`), unlike the DO interest
// detail whose :id is a Salesforce string.

export const createWellDetailResourceAtom = (href: string | undefined) =>
  createResourceAtom<MatchReviewResponse>(href);

export const wellDetailUrl = (
  documentId: number | string,
  wellId: number | string
) => `/documents/${documentId}/division_order_structuring/wells/${wellId}`;

// ── Lifecycle mutations (SHARED buildSectionMutationAtom) ─────────────────────
//
// Each mutation atom takes a single { url, method, data } arg. The setter
// returns the request Response so callers can branch on success/error.

/** Confirm a chosen suggestion (POST resources.confirmation.href). */
export const confirmMatchAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Revert a confirmation (DELETE resources.confirmation.href). */
export const revertMatchAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Change Match — pick existing / create imported (POST resources.matchCandidate.href). */
export const changeMatchAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Rerun the well-matching agent (POST resources.suggestionRerun.href). */
export const rerunSuggestionsAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();

/** Add a well — existing or imported (POST .../division_order_structuring/wells). */
export const addWellAtom = buildSectionMutationAtom<WellLifecycleResponse>();

/** Edit a staged well (PATCH .../division_order_structuring/wells/:id). */
export const editWellAtom = buildSectionMutationAtom<WellLifecycleResponse>();

/** Remove a well (DELETE .../division_order_structuring/wells/:id). */
export const removeWellAtom = buildSectionMutationAtom<WellLifecycleResponse>();

/** Restore a removed well (POST .../division_order_structuring/wells/:id/restoration). */
export const restoreWellAtom =
  buildSectionMutationAtom<WellLifecycleResponse>();
